/**
 * Field Name Overrides Editor Component (Editable)
 * Allows editing field name customizations for blueprints
 */

import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/InputDefault';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/DefaultSelectDropdown';
import { Checkbox } from '@/components/ui/checkbox';
import { ExportTemplate, FieldNameOverride } from '@/types/exportTemplates';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { extractBlueprintFields } from './utils/fieldExtractors';

interface FieldNameOverridesEditorProps {
  template: ExportTemplate;
  onChange: (template: ExportTemplate) => void;
  availableTemplates: any[];
}

export const FieldNameOverridesEditor = ({ template, onChange, availableTemplates }: FieldNameOverridesEditorProps) => {
  const { t } = usePlatformTranslation();
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<string>('');
  
  // Get the selected blueprint
  const selectedBlueprint = availableTemplates.find(bp => bp.id === selectedBlueprintId);
  
  // Get field name overrides for the selected blueprint
  const blueprintOverrides = template.fieldNameOverrides?.[selectedBlueprintId] || {};
  
  // Extract fields from the blueprint
  const blueprintFields = selectedBlueprint?.data ? extractBlueprintFields(selectedBlueprint.data) : [];
  
  const updateFieldOverride = (fieldName: string, override: Partial<FieldNameOverride>) => {
    const currentOverrides = template.fieldNameOverrides || {};
    const currentBlueprintOverrides = currentOverrides[selectedBlueprintId] || {};
    const currentFieldOverride = currentBlueprintOverrides[fieldName] || { exportName: fieldName, useExportName: false };
    
    const updatedFieldOverride = { ...currentFieldOverride, ...override };
    
    // If useExportName is false and exportName is empty or same as field name, remove the override
    if (!updatedFieldOverride.useExportName && (!updatedFieldOverride.exportName || updatedFieldOverride.exportName === fieldName)) {
      const { [fieldName]: _, ...remainingOverrides } = currentBlueprintOverrides;
      const updatedOverrides = {
        ...currentOverrides,
        [selectedBlueprintId]: remainingOverrides
      };
      
      // Clean up empty blueprint overrides
      if (Object.keys(remainingOverrides).length === 0) {
        const { [selectedBlueprintId]: __, ...remainingBlueprintOverrides } = updatedOverrides;
        onChange({
          ...template,
          fieldNameOverrides: Object.keys(remainingBlueprintOverrides).length > 0 ? remainingBlueprintOverrides : undefined
        });
      } else {
        onChange({
          ...template,
          fieldNameOverrides: updatedOverrides
        });
      }
    } else {
      onChange({
        ...template,
        fieldNameOverrides: {
          ...currentOverrides,
          [selectedBlueprintId]: {
            ...currentBlueprintOverrides,
            [fieldName]: updatedFieldOverride
          }
        }
      });
    }
  };
  
  return (
    <div>
      <Label className="text-sm font-semibold mb-2 block">Field Name Customization</Label>
      <p className="text-sm text-muted-foreground mb-3">
        Customize field names for specific blueprints in the database export. Enable "Use Custom" and provide a custom name to override the default naming convention for individual fields.
      </p>
      
      <div className="space-y-3">
        <div className="space-y-2">
          <Label className="text-sm">Select Blueprint</Label>
          <Select
            value={selectedBlueprintId}
            onValueChange={setSelectedBlueprintId}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose a blueprint to customize field names" />
            </SelectTrigger>
            <SelectContent>
              {availableTemplates.map(blueprint => (
                <SelectItem key={blueprint.id} value={blueprint.id}>
                  {blueprint.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {selectedBlueprintId && (
          <div className="border rounded overflow-hidden">
            {blueprintFields.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-accent">
                  <tr>
                    <th className="text-left p-2 font-medium">Field Name</th>
                    <th className="text-left p-2 font-medium">Export As</th>
                    <th className="text-center p-2 font-medium w-24">Use Custom</th>
                  </tr>
                </thead>
                <tbody>
                  {blueprintFields.map((fieldName) => {
                    const override = blueprintOverrides[fieldName] || { exportName: fieldName, useExportName: false };
                    return (
                      <tr key={fieldName} className="border-t hover:bg-accent/50">
                        <td className="p-2">{fieldName}</td>
                        <td className="p-2">
                          <Input
                            value={override.exportName || fieldName}
                            onChange={(e) => updateFieldOverride(fieldName, { exportName: e.target.value })}
                            placeholder={fieldName}
                            className="font-mono text-xs h-8"
                            disabled={!override.useExportName}
                          />
                        </td>
                        <td className="p-2 text-center">
                          <Checkbox
                            checked={override.useExportName}
                            onCheckedChange={(checked) => updateFieldOverride(fieldName, { useExportName: checked as boolean })}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="p-4 text-center text-muted-foreground text-sm">
                No fields found in this blueprint
              </div>
            )}
          </div>
        )}
        
        {!selectedBlueprintId && (
          <div className="p-4 border border-dashed rounded text-center text-muted-foreground text-sm">
            Select a blueprint to customize field names for export
          </div>
        )}
      </div>
    </div>
  );
};
